import { useEffect, useMemo, useState } from 'react';
import {
  listDocName,
  LISTS_DOC,
  listMetaSchema,
  personalListsDocName,
  type ListMeta,
} from '@awwyeah/shared';
import type * as Y from 'yjs';
import { getDocManager, useDocLocalLoaded, useYDoc, useYMap } from '../../sync';
import { useUser } from '../auth';
import { useMyDocAccess } from '../sharing';
import { mirrorListMeta, moveListMeta, readListMetaMirror, registryForAudience } from './listOps';

/**
 * The two list registries, merged (WS-15).
 *
 * Before WS-15 there was one `lists` doc holding every list's metadata. A private list
 * cannot live there — everybody can open `lists`, so a title in it is public — so each
 * member also has `lists:user:<id>`, which the sync hub gates by name. This hook is the
 * only place that knows there are two, and it hands every consumer one merged array.
 *
 * A list appearing in both (the write-then-delete window in `moveListMeta`, or two devices
 * racing) resolves personal-wins: the narrower answer is the safe one to show.
 *
 * There is a **third** source, and it exists because registries alone cannot express
 * "shared with me": a list Alice shares with Bob keeps its meta in *Alice's* personal
 * registry, which the sync hub rightly refuses Bob. So Bob learns which docs are his to
 * see from `GET /api/sharing`, and reads each list's name from the meta mirrored inside
 * the content doc itself (`mirrorListMeta`) — a doc the ACL already lets him open. Found
 * by two-user browser testing, where Bob could open the doc and still saw nothing.
 */

export type ListRegistry = 'family' | 'personal';

export type ListRegistrySource = ListRegistry | 'shared-with-me';

export interface RegisteredList {
  meta: ListMeta;
  registry: ListRegistrySource;
  /** True when this list's metadata lives in my personal registry. */
  personal: boolean;
  /** True when somebody else owns this list and shared it with me. */
  sharedWithMe?: boolean;
}

export interface ListRegistries {
  /** Every list I can see, merged and de-duplicated by id. */
  lists: RegisteredList[];
  /** Just the metas, for consumers that don't care where a list lives (the recipes picker). */
  metas: ListMeta[];
  familyMap: Y.Map<ListMeta>;
  /** `null` when there is no signed-in member (nothing renders in that state anyway). */
  personalMap: Y.Map<ListMeta> | null;
  /** Puts `meta` in the registry that matches `audience`, moving it if it isn't there. */
  placeInRegistry: (meta: ListMeta, audience: 'family' | 'private' | 'members') => void;
  /** Where a given list's meta currently lives. */
  registryOf: (listId: string) => ListRegistrySource | null;
  /**
   * Copies `meta` into its own content doc so members it is shared with can read the name
   * (see `mirrorListMeta`). Call it after every metadata write.
   */
  mirror: (meta: ListMeta) => void;
  /**
   * True once both registries have finished their local (IndexedDB) load. `lists` is
   * legitimately empty before this — offline-first means the family/personal registries
   * load asynchronously from IndexedDB, and right after an update-triggered reload a
   * consumer that trusts an empty `lists` immediately would flash "no lists yet" over a
   * household that has plenty (found in the WS-update-reload audit).
   */
  locallyLoaded: boolean;
}

export function useListRegistries(): ListRegistries {
  const { user } = useUser();

  const familyDoc = useYDoc(LISTS_DOC);
  // Hooks can't be conditional. With no signed-in member we deliberately open `lists`
  // twice — the DocManager is keyed by name, so that is the same doc and the same socket,
  // and `hasPersonal` below stops us counting its entries a second time.
  const personalDocName = user ? personalListsDocName(user.id) : LISTS_DOC;
  const personalDoc = useYDoc(personalDocName);
  const hasPersonal = Boolean(user);

  const { map: familyMap, entries: familyEntries } = useYMap<ListMeta>(familyDoc, 'lists');
  const { map: personalMapRaw, entries: personalEntries } = useYMap<ListMeta>(personalDoc, 'lists');
  const personalMap = hasPersonal ? personalMapRaw : null;

  // Hooks can't be conditional, same reasoning as the `useYDoc` pair above: always call
  // `useDocLocalLoaded` for both doc names, and ignore the personal one when there's no
  // signed-in member to have a personal registry.
  const familyLocallyLoaded = useDocLocalLoaded(LISTS_DOC);
  const personalLocallyLoaded = useDocLocalLoaded(personalDocName);
  const locallyLoaded = familyLocallyLoaded && (!hasPersonal || personalLocallyLoaded);

  const sharedWithMe = useSharedWithMeLists(user?.id ?? null);

  const lists = useMemo(() => {
    const byId = new Map<string, RegisteredList>();
    for (const [, meta] of familyEntries) {
      byId.set(meta.id, { meta, registry: 'family', personal: false });
    }
    for (const meta of sharedWithMe) {
      byId.set(meta.id, {
        meta,
        registry: 'shared-with-me',
        personal: false,
        sharedWithMe: true,
      });
    }
    if (hasPersonal) {
      // Personal last: it wins a duplicate, which is the narrower (safer) reading.
      for (const [, meta] of personalEntries) {
        byId.set(meta.id, { meta, registry: 'personal', personal: true });
      }
    }
    return [...byId.values()];
  }, [familyEntries, personalEntries, sharedWithMe, hasPersonal]);

  const metas = useMemo(() => lists.map((entry) => entry.meta), [lists]);

  const registryOf = (listId: string): ListRegistrySource | null =>
    lists.find((entry) => entry.meta.id === listId)?.registry ?? null;

  const mirror = (meta: ListMeta): void => {
    const handle = getDocManager().acquire(listDocName(meta.id));
    mirrorListMeta(handle.doc, meta);
    // The manager's 15s idle grace covers the flush; releasing immediately only stops the
    // socket being held open for a doc nothing is rendering.
    handle.release();
  };

  const placeInRegistry = (meta: ListMeta, audience: 'family' | 'private' | 'members'): void => {
    if (!personalMap) return;
    const target = registryForAudience(audience);
    const from = target === 'family' ? personalMap : familyMap;
    const to = target === 'family' ? familyMap : personalMap;
    mirror(meta); // the members it's shared with read the name from here
    if (!from.has(meta.id) && to.has(meta.id)) return; // already in the right place
    moveListMeta(from, to, meta, user?.id);
  };

  return {
    lists,
    metas,
    familyMap,
    personalMap,
    placeInRegistry,
    registryOf,
    mirror,
    locallyLoaded,
  };
}

/**
 * Lists other members have shared with me, by name.
 *
 * Enumeration comes from the ACL (`GET /api/sharing` returns rows where I am a listed
 * member); the name comes from the meta mirrored inside each content doc. Deliberately
 * imperative rather than a per-doc React "bridge" component: the set of docs is dynamic,
 * hooks counts are not, and every consumer of `useListRegistries` would otherwise have to
 * remember to render invisible children for this to work at all.
 */
function useSharedWithMeLists(userId: string | null): ListMeta[] {
  const { byDocName } = useMyDocAccess();
  const [metas, setMetas] = useState<ListMeta[]>([]);

  const docNames = useMemo(
    () =>
      userId
        ? [...byDocName.values()]
            .filter((row) => row.ownerId !== userId && row.memberIds.includes(userId))
            .map((row) => row.docName)
            .filter((name) => name.startsWith('list:'))
            .sort()
        : [],
    [byDocName, userId],
  );
  // Stable dependency: the array identity changes on every render, its contents rarely do.
  const key = docNames.join('|');

  useEffect(() => {
    if (docNames.length === 0) {
      setMetas([]);
      return;
    }
    const handles = docNames.map((name) => getDocManager().acquire(name));
    const read = (): void => {
      setMetas(
        handles.flatMap((handle) => {
          const parsed = listMetaSchema.safeParse(readListMetaMirror(handle.doc));
          return parsed.success ? [parsed.data] : [];
        }),
      );
    };
    const unsubscribes = handles.map((handle) => {
      const map = handle.doc.getMap('meta');
      const onChange = (): void => read();
      map.observe(onChange);
      return () => map.unobserve(onChange);
    });
    read();
    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
      for (const handle of handles) handle.release();
    };
    // Keyed on the joined doc names, not the array: its identity changes every render,
    // its contents almost never do, and re-acquiring every doc per render would thrash
    // the sockets. (`react-hooks/exhaustive-deps` is not configured in this repo.)
  }, [key, docNames]);

  return metas;
}

/**
 * One list's metadata, from whichever registry holds it, plus the `Y.Map` to write it
 * back to. The detail and shopping screens use this instead of reading the shared `lists`
 * doc directly — otherwise a private list opens as "list not found" for its own owner.
 */
export function useListMeta(listId: string | undefined): {
  meta: ListMeta | null;
  map: Y.Map<ListMeta> | null;
} {
  const registries = useListRegistries();
  if (!listId) return { meta: null, map: null };
  const entry = registries.lists.find((candidate) => candidate.meta.id === listId) ?? null;
  if (!entry) return { meta: null, map: null };
  // A list shared *with* me has no registry entry of mine. `null` rather than the family
  // map on purpose: writing there would republish its title to the whole household.
  if (entry.sharedWithMe) return { meta: entry.meta, map: null };
  return {
    meta: entry.meta,
    map: entry.personal ? registries.personalMap : registries.familyMap,
  };
}

/**
 * Just the visible list metas — what a consumer outside this module wants (the recipes
 * "add ingredients to a list" picker, which used to read the family `lists` doc directly
 * and would otherwise be blind to every private list).
 */
export function useVisibleLists(): ListMeta[] {
  return useListRegistries().metas;
}
